import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, Description } from '@mui/icons-material';
import { fazendaService, Fazenda } from '../services/fazendaService';
import { socioService, Socio } from '../services/socioService';
import { useAuth } from '../contexts/AuthContext';
import AutorizacaoFazendaDialog from '../components/AutorizacaoFazendaDialog';

/** Estado do formulário: Select MUI usa string vazia quando nada selecionado */
type FazendaFormData = Omit<Partial<Fazenda>, 'socio_id' | 'nome_socio'> & {
  socio_id: number | '';
};

const emptyForm = (): FazendaFormData => ({
  socio_id: '',
  nome_fazenda: '',
  proprietario_responsavel: '',
  proprietario_rg: '',
  cpf: '',
  endereco: '',
  cidade: '',
  estado: '',
  telefone: '',
  car: '',
  latitude: '',
  longitude: '',
  observacoes: '',
});

const Fazendas: React.FC = () => {
  const { user } = useAuth();
  const [fazendas, setFazendas] = useState<Fazenda[]>([]);
  const [socios, setSocios] = useState<Socio[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFazenda, setSelectedFazenda] = useState<Fazenda | null>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FazendaFormData>(emptyForm);
  const [autorizacaoOpen, setAutorizacaoOpen] = useState(false);
  const [autorizacaoFazenda, setAutorizacaoFazenda] = useState<Fazenda | null>(null);

  useEffect(() => {
    loadFazendas();
  }, []);

  useEffect(() => {
    if (user?.acesso_todos) {
      socioService.listar({ limit: 500 }).then(setSocios).catch(() => setSocios([]));
    }
  }, [user?.acesso_todos]);

  const loadFazendas = async () => {
    try {
      setLoading(true);
      const data = await fazendaService.listar();
      setFazendas(data);
    } catch (err: any) {
      setError('Erro ao carregar fazendas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (fazenda?: Fazenda) => {
    if (fazenda) {
      setSelectedFazenda(fazenda);
      setFormData({
        socio_id: fazenda.socio_id != null ? fazenda.socio_id : '',
        nome_fazenda: fazenda.nome_fazenda || '',
        proprietario_responsavel: fazenda.proprietario_responsavel || '',
        proprietario_rg: fazenda.proprietario_rg || '',
        cpf: fazenda.cpf || '',
        endereco: fazenda.endereco || '',
        cidade: fazenda.cidade || '',
        estado: fazenda.estado || '',
        telefone: fazenda.telefone || '',
        car: fazenda.car || '',
        latitude: fazenda.latitude ?? '',
        longitude: fazenda.longitude ?? '',
        observacoes: fazenda.observacoes || '',
      });
    } else {
      setSelectedFazenda(null);
      setFormData({
        socio_id: user?.acesso_todos ? '' : (user?.id ?? ''),
        nome_fazenda: '',
        proprietario_responsavel: '',
        proprietario_rg: '',
        cpf: '',
        endereco: '',
        cidade: '',
        estado: '',
        telefone: '',
        car: '',
        latitude: '',
        longitude: '',
        observacoes: '',
      });
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFazenda(null);
    setFormData(emptyForm());
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      let socioId =
        typeof formData.socio_id === 'number'
          ? formData.socio_id
          : formData.socio_id === ''
            ? NaN
            : Number(formData.socio_id);

      if (!user?.acesso_todos) {
        socioId = user!.id;
      } else if (!Number.isFinite(socioId) || socioId < 1) {
        setError('Selecione o associado vinculado à fazenda');
        return;
      }

      const dadosEnvio: Partial<Fazenda> = { ...formData, socio_id: socioId };

      if (dadosEnvio.latitude === '') {
        delete dadosEnvio.latitude;
      }
      if (dadosEnvio.longitude === '') {
        delete dadosEnvio.longitude;
      }
      delete (dadosEnvio as { nome_socio?: string }).nome_socio;

      if (selectedFazenda) {
        await fazendaService.atualizar(selectedFazenda.id!, dadosEnvio);
      } else {
        await fazendaService.criar(dadosEnvio as Fazenda);
      }

      handleCloseDialog();
      loadFazendas();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar fazenda');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja deletar esta fazenda?')) {
      return;
    }

    try {
      await fazendaService.deletar(id);
      loadFazendas();
    } catch (err: any) {
      setError('Erro ao deletar fazenda');
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1, sm: 0 }}
        mb={3}
      >
        <Typography variant="h4">{user?.acesso_todos ? 'Fazendas' : 'Minhas fazendas'}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Nova Fazenda
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : fazendas.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma fazenda cadastrada
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                {user?.acesso_todos && <TableCell>Associado</TableCell>}
                <TableCell>Responsável</TableCell>
                <TableCell>Cidade</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>CAR</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fazendas.map((fazenda) => (
                <TableRow key={fazenda.id}>
                  <TableCell>{fazenda.nome_fazenda}</TableCell>
                  {user?.acesso_todos && <TableCell>{fazenda.nome_socio || '-'}</TableCell>}
                  <TableCell>{fazenda.proprietario_responsavel || '-'}</TableCell>
                  <TableCell>{fazenda.cidade || '-'}</TableCell>
                  <TableCell>{fazenda.estado || '-'}</TableCell>
                  <TableCell>{fazenda.telefone || '-'}</TableCell>
                  <TableCell>{fazenda.car || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="success"
                      title="Gerar autorização de caça"
                      onClick={() => {
                        setAutorizacaoFazenda(fazenda);
                        setAutorizacaoOpen(true);
                      }}
                    >
                      <Description />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(fazenda)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(fazenda.id!)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedFazenda ? 'Editar Fazenda' : 'Nova Fazenda'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {user?.acesso_todos ? (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel id="fazenda-socio-label">Associado (titular)</InputLabel>
                  <Select
                    labelId="fazenda-socio-label"
                    label="Associado (titular)"
                    value={formData.socio_id === '' ? '' : String(formData.socio_id)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        socio_id: e.target.value ? Number(e.target.value) : '',
                      })
                    }
                  >
                    {socios.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.nome_completo} ({s.codigo || s.cpf})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Associado: <strong>{user?.nome}</strong> (esta fazenda fica vinculada ao seu cadastro)
                </Typography>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Nome da Fazenda"
                value={formData.nome_fazenda || ''}
                onChange={(e) => setFormData({ ...formData, nome_fazenda: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Proprietário/Responsável"
                value={formData.proprietario_responsavel || ''}
                onChange={(e) => setFormData({ ...formData, proprietario_responsavel: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="RG do proprietário"
                value={formData.proprietario_rg || ''}
                onChange={(e) => setFormData({ ...formData, proprietario_rg: e.target.value })}
                helperText="RG de quem assina a autorização de caça"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="CPF"
                value={formData.cpf || ''}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Endereço"
                value={formData.endereco || ''}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Cidade"
                value={formData.cidade || ''}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Estado"
                value={formData.estado || ''}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Telefone"
                value={formData.telefone || ''}
                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="CAR"
                value={formData.car || ''}
                onChange={(e) => setFormData({ ...formData, car: e.target.value })}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Latitude"
                value={formData.latitude ?? ''}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                placeholder={`Ex: 28°31'59,28" S`}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Longitude"
                value={formData.longitude ?? ''}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                placeholder={`Ex: 50°20'26,06" O`}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.observacoes || ''}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <AutorizacaoFazendaDialog
        open={autorizacaoOpen}
        fazendaId={autorizacaoFazenda?.id ?? null}
        fazendaNome={autorizacaoFazenda?.nome_fazenda || ''}
        onClose={() => {
          setAutorizacaoOpen(false);
          setAutorizacaoFazenda(null);
        }}
      />
    </Box>
  );
};

export default Fazendas;

